<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky bs-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.balanceSheet.title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ $t("finance.coa.expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ $t("finance.coa.collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>Excel</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.csv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ $t("finance.trialBalance.printPdf") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadReport">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ $t("finance.common.refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-text-field
            v-model="filters.date"
            type="date"
            :label="$t('finance.balanceSheet.asOfDate')"
            hide-details
            class="fin-field"
          ></v-text-field>
          <v-select
            v-model="filters.currency"
            :items="currencyOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.balanceSheet.currency')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-select
            v-model="filters.max_level"
            :items="levelOptions"
            item-text="text"
            item-value="value"
            :label="$t('finance.trialBalance.maxLevel')"
            hide-details
            class="fin-field"
          ></v-select>
          <v-btn small color="primary" @click="loadReport">{{ $t("finance.common.apply") }}</v-btn>
        </div>
      </div>

      <div
        v-if="hasReport && !report.totals.is_balanced"
        class="fin-alert-error bs-no-print"
      >
        <v-icon small color="#c62828">warning</v-icon>
        {{ $t("finance.balanceSheet.unbalancedMessage") }}
        ({{ $t("finance.trialBalance.difference") }}: {{ formatValue(report.totals.difference) }})
      </div>

      <div v-if="hasReport" class="fin-summary bs-no-print">
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.balanceSheet.totalAssets") }}</span>
          <span class="fin-stat-value">{{ formatValue(report.totals.total_assets) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.balanceSheet.totalLiabilities") }}</span>
          <span class="fin-stat-value">{{ formatValue(report.totals.total_liabilities) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.balanceSheet.totalEquity") }}</span>
          <span class="fin-stat-value">{{ formatValue(report.totals.total_equity) }}</span>
        </div>
        <div class="fin-stat">
          <span class="fin-stat-label">{{ $t("finance.balanceSheet.currentPeriodResult") }}</span>
          <span class="fin-stat-value">{{ formatValue(report.totals.current_period_result) }}</span>
        </div>
        <v-chip small :color="report.totals.is_balanced ? 'green' : 'red'" text-color="white">
          <v-icon small class="me-1">{{ report.totals.is_balanced ? "check_circle" : "warning" }}</v-icon>
          {{
            report.totals.is_balanced
              ? $t("finance.balanceSheet.balancedShort")
              : $t("finance.balanceSheet.unbalancedShort")
          }}
        </v-chip>
        <span v-if="report.totals.is_balanced" class="fin-stat-ok">
          <v-icon small color="#2e7d32">check_circle</v-icon>
          {{ $t("finance.balanceSheet.balancedMessage") }}
        </span>
      </div>

      <div class="bs-table-wrap fin-table-wrap">
        <table class="bs-tree-table fin-table">
          <thead>
            <tr>
              <th>{{ $t("finance.common.code") }}</th>
              <th class="bs-col-name">{{ $t("finance.balanceSheet.item") }}</th>
              <th class="fin-num-head">{{ $t("finance.balanceSheet.value") }}</th>
              <th class="bs-col-notes">{{ $t("finance.trialBalance.notes") }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in displayRows">
              <tr
                v-if="row.type === 'account'"
                :key="'a-' + row.account.id"
                :class="rowClasses(row)"
              >
                <td class="fin-code">{{ row.account.code }}</td>
                <td class="bs-col-name">
                  <span class="bs-indent" :style="{ 'padding-inline-start': row.depth * 26 + 'px' }">
                    <v-btn
                      v-if="row.hasChildren"
                      icon
                      small
                      class="bs-toggle bs-no-print"
                      @click="toggleNode(row.account.id)"
                    >
                      <v-icon small>{{ isExpanded(row.account.id) ? "remove" : "add" }}</v-icon>
                    </v-btn>
                    <span v-else class="bs-toggle-spacer"></span>
                    <span class="fin-ellipsis" :title="localizedAccountName(row.account)">{{ localizedAccountName(row.account) }}</span>
                  </span>
                </td>
                <td class="fin-num" :class="{ 'bs-negative': row.account.value < 0 }">
                  {{ formatValue(row.account.value) }}
                </td>
                <td class="bs-col-notes">{{ rowNote(row.account) }}</td>
              </tr>
              <tr v-else :key="'t-' + row.section + '-' + index" class="bs-section-total-row">
                <td></td>
                <td>{{ sectionTotalLabel(row.section) }}</td>
                <td class="fin-num">{{ formatValue(sectionTotalValue(row.section)) }}</td>
                <td></td>
              </tr>
            </template>
            <tr v-if="!displayRows.length && !loading">
              <td colspan="4" class="fin-empty">{{ $t("finance.coa.noResults") }}</td>
            </tr>
          </tbody>
          <tfoot v-if="hasReport">
            <tr class="bs-totals-row">
              <td></td>
              <td>{{ $t("finance.balanceSheet.liabilitiesPlusEquity") }}</td>
              <td class="fin-num">{{ formatValue(report.totals.liabilities_and_equity) }}</td>
              <td></td>
            </tr>
            <tr class="bs-totals-row" :class="report.totals.is_balanced ? 'bs-eq-ok' : 'bs-eq-bad'">
              <td></td>
              <td>{{ $t("finance.trialBalance.difference") }}</td>
              <td class="fin-num">{{ formatValue(report.totals.difference) }}</td>
              <td>
                {{
                  report.totals.is_balanced
                    ? $t("finance.balanceSheet.balancedMessage")
                    : $t("finance.balanceSheet.unbalancedMessage")
                }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script>
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import { companyNow, companyToday } from "@/util/companyTime";

export default {
  name: "BalanceSheet",
  title() {
    return this.$t("finance.balanceSheet.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent },
  data() {
    // Final-version doc 2026-10-05: the company (Damascus) day on the
    // server-synced clock, not the PC calendar.
    const today = companyToday();

    return {
      loading: false,
      report: null,
      expandedIds: {},
      filters: {
        // Financial position as of today by default.
        date: today,
        // Doc 4.2: value display in the new (redenominated) or old currency.
        currency: "new",
        max_level: null,
      },
    };
  },
  computed: {
    hasReport() {
      return !!(this.report && this.report.totals);
    },
    reportRows() {
      return (this.report && this.report.rows) || [];
    },
    rowsById() {
      const map = {};
      this.reportRows.forEach((row) => {
        map[row.id] = row;
      });
      return map;
    },
    childrenByParent() {
      // Server rows arrive in tree order (assets, then liabilities, then
      // equity; parents before children). A parent missing from the payload
      // (e.g. cut by max_level) turns its children into roots.
      const map = {};
      this.reportRows.forEach((row) => {
        const parentId = row.parent_id && this.rowsById[row.parent_id] ? row.parent_id : 0;
        if (!map[parentId]) {
          map[parentId] = [];
        }
        map[parentId].push(row);
      });
      return map;
    },
    // Account rows honoring expand state, with a section-total row appended
    // after each top-level section subtree.
    displayRows() {
      const rows = [];
      const walk = (parentId, depth, seen) => {
        (this.childrenByParent[parentId] || []).forEach((account) => {
          if (seen[account.id]) {
            return; // cycle guard
          }
          const hasChildren = !!(this.childrenByParent[account.id] || []).length;
          rows.push({ type: "account", account, depth, hasChildren });
          if (hasChildren && this.expandedIds[account.id]) {
            const nextSeen = Object.assign({}, seen);
            nextSeen[account.id] = true;
            walk(account.id, depth + 1, nextSeen);
          }
        });
      };

      const emittedSections = {};
      (this.childrenByParent[0] || []).forEach((root) => {
        const hasChildren = !!(this.childrenByParent[root.id] || []).length;
        rows.push({ type: "account", account: root, depth: 0, hasChildren });
        if (hasChildren && this.expandedIds[root.id]) {
          const seen = {};
          seen[root.id] = true;
          walk(root.id, 1, seen);
        }
        if (root.section && !emittedSections[root.section]) {
          emittedSections[root.section] = true;
          rows.push({ type: "total", section: root.section });
        }
      });
      return rows;
    },
    levelOptions() {
      const options = [{ text: this.$t("finance.trialBalance.allLevels"), value: null }];
      for (let level = 1; level <= 8; level++) {
        options.push({ text: String(level), value: level });
      }
      return options;
    },
    currencyOptions() {
      return [
        { text: this.$t("finance.balanceSheet.currencyNew"), value: "new" },
        { text: this.$t("finance.balanceSheet.currencyOld"), value: "old" },
      ];
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    buildParams(extra) {
      const params = {};
      if (this.filters.date) params.date = this.filters.date;
      if (this.filters.max_level) params.max_level = this.filters.max_level;
      return Object.assign(params, extra || {});
    },
    loadReport() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_BALANCE_SHEET, { params: this.buildParams() })
        .then(
          function (response) {
            this.report = response.data.data || null;
            // Accountants read the whole statement by default.
            this.expandAll();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.balanceSheet.failedLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    isExpanded(id) {
      return !!this.expandedIds[id];
    },
    toggleNode(id) {
      const next = Object.assign({}, this.expandedIds);
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.expandedIds = next;
    },
    expandAll() {
      const next = {};
      this.reportRows.forEach((row) => {
        next[row.id] = true;
      });
      this.expandedIds = next;
    },
    collapseAll() {
      this.expandedIds = {};
    },
    rowClasses(row) {
      return {
        "bs-level-1": row.account.level === 1,
        "bs-level-2": row.account.level === 2,
        "bs-parent-row": row.hasChildren,
        "bs-computed-row": !!row.account.is_computed,
        "bs-inactive-row": row.account.is_active === false,
      };
    },
    localizedAccountName(account) {
      if (!account) {
        return "-";
      }

      if (this.$i18n && this.$i18n.locale === "ar" && account.name_ar) {
        return account.name_ar;
      }

      return account.name || account.name_ar || "-";
    },
    localizedProvisionName(account) {
      if (this.$i18n && this.$i18n.locale === "ar" && account.provision_name_ar) {
        return account.provision_name_ar;
      }

      return account.provision_name || account.provision_name_ar || "";
    },
    // Doc 4.2 currency filter: "new" shows the redenominated value (÷100,
    // same as the money mixin), "old" shows the stored value as-is.
    formatValue(value) {
      if (this.filters.currency === "old") {
        return Number(value || 0).toLocaleString("en", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      return this.formatMoney(value);
    },
    rowNote(account) {
      if (account.is_computed) {
        return this.$t("finance.balanceSheet.computedNote");
      }
      if (account.provision_code) {
        return this.$t("finance.balanceSheet.netOfProvision", {
          name: this.localizedProvisionName(account),
          amount: this.formatValue(account.provision_value),
        });
      }
      return "";
    },
    sectionTotalLabel(section) {
      const map = {
        assets: this.$t("finance.balanceSheet.totalAssets"),
        liabilities: this.$t("finance.balanceSheet.totalLiabilities"),
        equity: this.$t("finance.balanceSheet.totalEquity"),
      };
      return map[section] || section;
    },
    sectionTotalValue(section) {
      if (!this.hasReport) {
        return 0;
      }
      const map = {
        assets: this.report.totals.total_assets,
        liabilities: this.report.totals.total_liabilities,
        equity: this.report.totals.total_equity,
      };
      return map[section] || 0;
    },
    exportFileStamp() {
      // Final-version doc 2026-10-05: company day, not the PC calendar.
      return companyToday();
    },
    asOfLabel() {
      return this.$t("finance.balanceSheet.asOfDate") + " " + (this.filters.date || "—");
    },
    downloadCsv() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_BALANCE_SHEET, {
          params: this.buildParams({ format: "csv" }),
          responseType: "blob",
        })
        .then(
          function (response) {
            const blob = new Blob([response.data], { type: response.headers["content-type"] || "text/csv" });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = "balance-sheet-" + this.exportFileStamp() + ".csv";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.reports.failedDownloadReport")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    // The whole loaded report in tree order (ignores expand state): exports
    // always carry the full statement, not whatever is on screen.
    fullDisplayRows() {
      const rows = [];
      const walk = (parentId, depth, seen) => {
        (this.childrenByParent[parentId] || []).forEach((account) => {
          if (seen[account.id]) {
            return; // cycle guard
          }
          rows.push({ type: "account", account, depth });
          const nextSeen = Object.assign({}, seen);
          nextSeen[account.id] = true;
          walk(account.id, depth + 1, nextSeen);
        });
      };
      const emittedSections = {};
      (this.childrenByParent[0] || []).forEach((root) => {
        rows.push({ type: "account", account: root, depth: 0 });
        const seen = {};
        seen[root.id] = true;
        walk(root.id, 1, seen);
        if (root.section && !emittedSections[root.section]) {
          emittedSections[root.section] = true;
          rows.push({ type: "total", section: root.section });
        }
      });
      return rows;
    },
    exportExcel() {
      if (!this.hasReport) {
        return;
      }
      // Real .xlsx built client-side from the loaded report (doc 4.3), in
      // tree order with section styling; the CSV endpoint stays available
      // for raw integrations.
      const rows = this.fullDisplayRows();
      const totals = this.report.totals;
      const isArabic = this.$i18n && this.$i18n.locale === "ar";
      const useNewCurrency = this.filters.currency !== "old";

      import(/* webpackChunkName: "xlsx" */ "xlsx-js-style")
        .then(
          function (module) {
            const XLSX = module.default || module;
            const moneyFormat = "#,##0.00";
            // Stored amounts carry the pre-redenomination scale; the export
            // follows the currency filter (÷100 for the new currency).
            const money = (value) => (useNewCurrency ? Number(value || 0) / 100 : Number(value || 0));

            const header = [
              this.$t("finance.common.code"),
              this.$t("finance.balanceSheet.item"),
              this.$t("finance.coa.level"),
              this.$t("finance.balanceSheet.value"),
              this.$t("finance.trialBalance.notes"),
            ];
            const data = rows.map(
              function (row) {
                if (row.type === "total") {
                  return [
                    "",
                    this.sectionTotalLabel(row.section),
                    "",
                    money(this.sectionTotalValue(row.section)),
                    "",
                  ];
                }
                return [
                  String(row.account.code || ""),
                  this.localizedAccountName(row.account),
                  Number(row.account.level || 1),
                  money(row.account.value),
                  this.rowNote(row.account),
                ];
              }.bind(this)
            );
            const footer = [
              ["", this.$t("finance.balanceSheet.liabilitiesPlusEquity"), "", money(totals.liabilities_and_equity), ""],
              [
                "",
                this.$t("finance.trialBalance.difference"),
                "",
                money(totals.difference),
                totals.is_balanced
                  ? this.$t("finance.balanceSheet.balancedMessage")
                  : this.$t("finance.balanceSheet.unbalancedMessage"),
              ],
            ];

            const ws = XLSX.utils.aoa_to_sheet([header].concat(data, footer));
            ws["!cols"] = [{ wch: 14 }, { wch: 52 }, { wch: 8 }, { wch: 18 }, { wch: 52 }];

            const thin = { style: "thin", color: { rgb: "9E9E9E" } };
            const borders = { top: thin, bottom: thin, left: thin, right: thin };
            const totalRowCount = data.length + footer.length;
            for (let r = 0; r <= totalRowCount; r++) {
              for (let c = 0; c < header.length; c++) {
                const cell = ws[XLSX.utils.encode_cell({ r: r, c: c })];
                if (!cell) {
                  continue;
                }
                if (r === 0) {
                  cell.s = {
                    font: { bold: true },
                    fill: { patternType: "solid", fgColor: { rgb: "ECEFF1" } },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                  continue;
                }
                if (r > data.length) {
                  // Footer: equation rows.
                  cell.s = {
                    font: { bold: true },
                    fill: {
                      patternType: "solid",
                      fgColor: { rgb: totals.is_balanced ? "E8F5E9" : "FFCDD2" },
                    },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                  if (c === 3) {
                    cell.z = moneyFormat;
                  }
                  continue;
                }
                const row = rows[r - 1];
                if (row.type === "total") {
                  cell.s = {
                    font: { bold: true },
                    fill: { patternType: "solid", fgColor: { rgb: "E3F2FD" } },
                    border: borders,
                    alignment: { vertical: "center" },
                  };
                } else {
                  const bold = Number(row.account.level || 1) <= 2;
                  cell.s = {
                    font: { bold: bold, italic: !!row.account.is_computed },
                    border: borders,
                    alignment:
                      c === 1
                        ? { indent: Math.min(row.depth, 15), vertical: "center" }
                        : { vertical: "center" },
                  };
                }
                if (c === 3) {
                  cell.z = moneyFormat;
                }
              }
            }

            const wb = XLSX.utils.book_new();
            // Sheet reads right-to-left for the Arabic report.
            wb.Workbook = { Views: [{ RTL: !!isArabic }] };
            XLSX.utils.book_append_sheet(wb, ws, this.$t("finance.balanceSheet.pageTitle"));
            XLSX.writeFile(wb, "balance-sheet-" + this.exportFileStamp() + ".xlsx");
          }.bind(this)
        )
        .catch(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.reports.failedDownloadReport"), "red");
          }.bind(this)
        );
    },
    escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    exportPrint() {
      if (!this.hasReport) {
        return;
      }
      // Dedicated print document (doc 4.3: print + save-as-PDF): no app
      // chrome, repeated table header per page, RTL-aware. The browser's
      // "Save as PDF" printer renders the Arabic faithfully.
      const rows = this.fullDisplayRows();
      const totals = this.report.totals;
      const isArabic = this.$i18n && this.$i18n.locale === "ar";
      const esc = this.escapeHtml;
      const title = this.$t("finance.balanceSheet.pageTitle");
      // Final-version doc 2026-10-05: company (Damascus) time, not the PC's.
      const stamp = companyNow().format("YYYY-MM-DD HH:mm:ss");

      const headerCells = [
        this.$t("finance.common.code"),
        this.$t("finance.balanceSheet.item"),
        this.$t("finance.balanceSheet.value"),
        this.$t("finance.trialBalance.notes"),
      ]
        .map(function (label) {
          return "<th>" + esc(label) + "</th>";
        })
        .join("");

      const bodyRows = rows
        .map(
          function (row) {
            if (row.type === "total") {
              return (
                '<tr class="section-total">' +
                "<td></td>" +
                "<td>" + esc(this.sectionTotalLabel(row.section)) + "</td>" +
                '<td class="num">' + esc(this.formatValue(this.sectionTotalValue(row.section))) + "</td>" +
                "<td></td>" +
                "</tr>"
              );
            }
            const level = Number(row.account.level || 1);
            const classes = [];
            if (level === 1) classes.push("lvl-1");
            if (level === 2) classes.push("lvl-2");
            if (row.account.is_computed) classes.push("computed");
            return (
              '<tr class="' + classes.join(" ") + '">' +
              '<td class="code">' + esc(row.account.code) + "</td>" +
              '<td><span style="padding-inline-start:' + row.depth * 16 + 'px">' +
              esc(this.localizedAccountName(row.account)) + "</span></td>" +
              '<td class="num">' + esc(this.formatValue(row.account.value)) + "</td>" +
              "<td>" + esc(this.rowNote(row.account)) + "</td>" +
              "</tr>"
            );
          }.bind(this)
        )
        .join("");

      const footerRows =
        '<tr class="totals">' +
        "<td></td>" +
        "<td>" + esc(this.$t("finance.balanceSheet.liabilitiesPlusEquity")) + "</td>" +
        '<td class="num">' + esc(this.formatValue(totals.liabilities_and_equity)) + "</td>" +
        "<td></td>" +
        "</tr>" +
        '<tr class="totals ' + (totals.is_balanced ? "ok" : "bad") + '">' +
        "<td></td>" +
        "<td>" + esc(this.$t("finance.trialBalance.difference")) + "</td>" +
        '<td class="num">' + esc(this.formatValue(totals.difference)) + "</td>" +
        "<td>" +
        esc(
          totals.is_balanced
            ? this.$t("finance.balanceSheet.balancedMessage")
            : this.$t("finance.balanceSheet.unbalancedMessage")
        ) +
        "</td>" +
        "</tr>";

      const doc =
        "<!doctype html>" +
        '<html dir="' + (isArabic ? "rtl" : "ltr") + '" lang="' + (isArabic ? "ar" : "en") + '">' +
        '<head><meta charset="utf-8"><title>' + esc(title) + "</title><style>" +
        'body{font-family:"Segoe UI",Tahoma,Arial,sans-serif;color:#111;margin:0;padding:24px;}' +
        "header{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #111;padding-bottom:8px;margin-bottom:14px;}" +
        "h1{font-size:20px;margin:0;}" +
        ".meta{font-size:12px;color:#555;text-align:end;}" +
        "table{width:100%;border-collapse:collapse;font-size:12px;}" +
        "th,td{border:1px solid #888;padding:4px 8px;text-align:start;vertical-align:top;}" +
        "thead{display:table-header-group;}" +
        "thead th{background:#eee;font-weight:700;}" +
        "tr{page-break-inside:avoid;}" +
        ".lvl-1 td{font-weight:700;font-size:13px;background:#f3f3f3;}" +
        ".lvl-2 td{font-weight:700;}" +
        ".computed td{font-style:italic;background:#e3f2fd;}" +
        ".section-total td{font-weight:700;background:#e3f2fd;}" +
        ".totals td{font-weight:700;}" +
        ".totals.ok td{background:#e8f5e9;}" +
        ".totals.bad td{background:#ffcdd2;}" +
        ".code{white-space:nowrap;}" +
        ".num{text-align:end;direction:ltr;unicode-bidi:embed;white-space:nowrap;}" +
        "@page{size:A4;margin:12mm;}" +
        "</style></head><body>" +
        "<header><h1>" + esc(title) + '</h1><div class="meta">' +
        esc(this.asOfLabel()) + "<br>" +
        esc(this.$t("finance.coa.exportedAt")) + ": " + esc(stamp) + "</div></header>" +
        "<table><thead><tr>" + headerCells + "</tr></thead><tbody>" + bodyRows + footerRows + "</tbody></table>" +
        "</body></html>";

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.reports.failedDownloadReport"), "red");
        return;
      }
      printWindow.document.write(doc);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(function () {
        printWindow.print();
      }, 400);
    },
  },
};
</script>

<style scoped>
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
.bs-table-wrap {
  --fin-table-max-h: calc(100vh - 250px);
  min-height: 320px;
}
.bs-col-notes {
  white-space: normal;
  min-width: 220px;
}
.bs-parent-row td {
  background: #f5f7fa;
}
/* Doc 4.1 visual rule: main sections bold; level 1 slightly larger. */
.bs-level-1 td {
  font-weight: 700;
  font-size: 14px;
}
.bs-level-2 td {
  font-weight: 700;
}
/* The computed current-period result row stands apart from real accounts. */
.bs-computed-row td {
  font-style: italic;
  background: #e3f2fd;
}
.bs-inactive-row td {
  color: rgba(0, 0, 0, 0.4);
}
.bs-section-total-row td {
  font-weight: 700;
  background: #e3f2fd;
}
.bs-indent {
  display: inline-flex;
  align-items: center;
}
.bs-toggle {
  margin: 0;
  margin-inline-end: 4px;
  height: 22px;
  width: 22px;
}
.bs-toggle-spacer {
  display: inline-block;
  width: 26px;
}
.bs-negative {
  color: #c62828;
}
.bs-totals-row td {
  font-weight: 700;
  background: #eceff1;
}
.bs-eq-ok td {
  background: #e8f5e9;
}
.bs-eq-bad td {
  background: #ffcdd2;
}
@media print {
  .bs-no-print {
    display: none !important;
  }
}
</style>
